import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { liveSportsMenuQuery } from '@/api/queries';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { translateSportsLabel } from '@/i18n/sports-label';
import { cn } from '@/lib/cn';
import { sportIcon, toSlug } from '@/shell/sports-menu-utils';
import { HorizontalScrollRow, SportIcon } from '@/ui';

export interface LiveSportsMenuSliderProps {
  selectedSportSlug: string;
  onSportSelect: (sportSlug: string) => void;
  className?: string;
}

/**
 * Horizontal live sports filter (Figma SportsMenuSlider). Shown on desktop and mobile
 * Live pages with per-sport live event counts.
 */
export function LiveSportsMenuSlider({
  selectedSportSlug,
  onSportSelect,
  className,
}: LiveSportsMenuSliderProps) {
  const { t, i18n } = useTranslation();
  const { selectedLanguage, isLoadingTranslations } = useTranslations();
  const { data: sports, isPending, isError } = useQuery(liveSportsMenuQuery());

  const sportsTranslationsReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'sports');
  const shouldHoldForTranslations = !sportsTranslationsReady && isLoadingTranslations;

  const sortedSports = sports
    ? [...sports].filter((sport) => !sport.Blocked).sort((a, b) => a.Position - b.Position)
    : [];

  if (isPending || shouldHoldForTranslations) {
    return (
      <div className={cn(className)} aria-busy="true">
        <HorizontalScrollRow aria-label={t('Sports', { defaultValue: 'Sports' })} className="items-center">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="h-14 min-w-[72px] shrink-0 animate-pulse rounded-md bg-bg-elevated" />
          ))}
        </HorizontalScrollRow>
      </div>
    );
  }

  if (isError) {
    return (
      <p className={cn('px-3 text-body-s text-text-secondary', className)}>
        {t('Could not load live sports menu.', { defaultValue: 'Could not load live sports menu.' })}
      </p>
    );
  }

  return (
    <nav
      aria-label={t('Live sports menu', { defaultValue: 'Live sports menu' })}
      className={cn('overflow-clip', className)}
    >
      <HorizontalScrollRow
        aria-label={t('Sports', { defaultValue: 'Sports' })}
        className="items-center"
      >
        {sortedSports.map((sport) => {
          const slug = toSlug(sport.Name);
          const selected = slug === selectedSportSlug;
          const label = translateSportsLabel(i18n, t, sport.Id, sport.Name);

          return (
            <button
              key={sport.Id}
              type="button"
              role="listitem"
              onClick={() => onSportSelect(slug)}
              className={cn(
                'relative flex min-w-[72px] shrink-0 flex-col items-center justify-center gap-1 rounded-md px-2 py-2 leading-none',
                selected && 'bg-bg-elevated',
              )}
            >
              <span className="flex size-6 shrink-0 items-center justify-center">
                <SportIcon
                  name={sportIcon(slug)}
                  size={24}
                  className={selected ? 'text-icon-default' : 'text-icon-muted'}
                />
              </span>
              <span
                className={cn(
                  'text-label-s leading-4 whitespace-nowrap tracking-[0.2px]',
                  selected ? 'text-text-primary' : 'text-text-secondary',
                )}
              >
                {label}
              </span>
              <span
                className={cn(
                  'absolute top-[3px] right-1 text-[10px] leading-none',
                  selected ? 'text-text-on-action' : 'text-text-secondary',
                )}
              >
                {sport.Count}
              </span>
            </button>
          );
        })}
      </HorizontalScrollRow>
    </nav>
  );
}
